import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import { harmLabel } from "@/lib/constants";
import { Plus, FileText, Clock, Buildings, Lightning, Scales, Target, Files, Lock, ArrowRight } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

const DOCS_BG = "https://static.prod-images.emergentagent.com/jobs/65306284-f0b7-4b09-9326-a0975e697622/images/e186a3299b85ef21137319f64c6f38a50087fbf2b37684fa3254c72a803992e6.png";

export default function Dashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPaid = !!(user?.is_paid || user?.is_admin);

  useEffect(() => {
    (async () => {
      try {
        // Refresh user status (in case admin flipped is_paid)
        try { await api.get("/auth/me"); } catch (err) { logger.error("auth/me refresh failed", err); }
        const [c, s] = await Promise.all([api.get("/cases"), api.get("/stats")]);
        setCases(c.data);
        setStats(s.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="dashboard-page">
      <Nav />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Upgrade nag banner for unpaid users */}
        {!isPaid && (
          <a
            href="https://eaglefish.gumroad.com/l/txtqey"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#FF3366] bg-[#FF3366]/10 hover:bg-[#FF3366]/15 p-4 mb-8 transition-colors"
            data-testid="upgrade-nag-banner"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#FF3366] flex items-center justify-center flex-shrink-0">
                <Lock size={18} weight="bold" color="#fff" />
              </div>
              <div>
                <div className="font-heading font-bold uppercase tracking-tight text-base leading-tight">
                  Your Command Center is locked.
                </div>
                <div className="font-mono-sf text-xs text-neutral-300 mt-1">
                  Activate your Full Arsenal for <span className="text-[#00FFCC] font-bold">$97</span> — one-time, lifetime access.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF3366] group-hover:text-white pl-14 sm:pl-0">
              Activate Now <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </a>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-3">// command center</div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-6xl leading-none">
              Active Operations.
            </h1>
          </div>
          <Link
            to="/cases/new"
            className="inline-flex items-center justify-center gap-3 h-12 px-6 bg-[#FF3366] hover:bg-[#FF5248] text-white font-mono-sf text-xs uppercase tracking-[0.25em] font-bold transition-colors"
            data-testid="dashboard-new-case-btn"
          >
            <Plus size={16} weight="bold" /> New Case
          </Link>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#1A1A1A] divide-x divide-y md:divide-y-0 divide-[#1A1A1A] mb-12" data-testid="stats-grid">
          {[
            { i: Target, l: "Total Cases", v: stats?.total_cases ?? "—" },
            { i: Lightning, l: "Active", v: stats?.active_cases ?? "—" },
            { i: Files, l: "Drafts", v: stats?.drafts_generated ?? "—" },
            { i: Scales, l: "Actions Logged", v: stats?.actions_logged ?? "—" },
            { i: Buildings, l: "Agencies Hit", v: stats?.agencies_contacted ?? "—" },
            { i: Clock, l: "Days Fighting", v: stats?.days_fighting ?? "—" },
          ].map((s) => {
            const Icon = s.i;
            return (
              <div key={s.l} className="p-5 md:p-6" data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{s.l}</span>
                  <Icon size={16} weight="bold" className="text-[#FF3366]" />
                </div>
                <div className="font-mono-sf text-3xl font-bold text-white">{s.v}</div>
              </div>
            );
          })}
        </div>

        {/* Cases list */}
        <div className="flex items-baseline gap-4 mb-6">
          <h2 className="font-heading font-bold uppercase tracking-tight text-2xl">Cases</h2>
          <div className="flex-1 h-px bg-[#1A1A1A]" />
          <span className="font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-500">{cases.length} total</span>
        </div>

        {loading ? (
          <div className="border border-[#1A1A1A] p-12 text-center font-mono-sf text-xs uppercase tracking-wider text-neutral-500" data-testid="loading-state">
            Loading operations...
          </div>
        ) : cases.length === 0 ? (
          <div className="relative border border-[#1A1A1A] overflow-hidden" data-testid="empty-state">
            <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${DOCS_BG})` }} />
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative p-12 md:p-16 text-center">
              <FileText size={48} weight="bold" className="text-[#FF3366] mx-auto mb-6" />
              <h3 className="font-heading font-bold uppercase tracking-tight text-3xl mb-3">No cases yet.</h3>
              <p className="text-neutral-400 max-w-md mx-auto mb-8 text-sm">
                Identify a corporate adversary and let the AI draft your six-front counter-strike.
              </p>
              <Link
                to="/cases/new"
                className="inline-flex items-center gap-3 h-12 px-6 bg-[#FF3366] hover:bg-[#FF5248] text-white font-mono-sf text-xs uppercase tracking-[0.25em] font-bold transition-colors"
                data-testid="empty-new-case-btn"
              >
                <Plus size={16} weight="bold" /> Deploy First Case
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="cases-grid">
            {cases.map((c) => (
              <Link
                to={`/cases/${c.id}`}
                key={c.id}
                className="bg-[#0D0D0D] hover:bg-[#1A1A1A] p-6 transition-colors group"
                data-testid={`case-card-${c.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#FF3366] border border-[#FF3366]/40 px-2 py-1">
                    <span className="w-1.5 h-1.5 bg-[#FF3366] animate-pulse" />
                    {c.status}
                  </span>
                  <span className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-600">
                    {new Date(c.created_at).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="font-heading font-bold uppercase tracking-tight text-xl mb-1 group-hover:text-[#FF3366] transition-colors line-clamp-2">
                  {c.title}
                </h3>
                <div className="font-mono-sf text-xs uppercase tracking-wider text-neutral-400 mb-4">
                  vs. {c.corporation}
                </div>
                <div className="text-xs text-neutral-500 mb-4 font-mono-sf">{harmLabel(c.harm_type)}</div>
                <div className="flex items-center justify-between pt-4 border-t border-[#1A1A1A]">
                  <div>
                    <div className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-600">Drafts</div>
                    <div className="font-mono-sf text-lg font-bold">{Object.keys(c.drafts || {}).length}/6</div>
                  </div>
                  <div>
                    <div className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-600">Damages</div>
                    <div className="font-mono-sf text-lg font-bold text-[#FF3366]">${(c.estimated_damages || 0).toLocaleString()}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
